import React, { useEffect, useState } from 'react';
import { Send, Loader2, Download, CalendarClock, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { exportAPI } from '../services/api';

const eur = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const STATUS = { sent: ['text-green-700', 'Envoyé'], generated: ['text-gray-600', 'Généré'], no_recipient: ['text-amber-700', 'Aucun destinataire'], email_failed: ['text-red-700', "Échec e-mail"] };

// Admin export panel: automatic monthly send to the accountant (history + send now)
const AdminExportAuto = () => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => exportAPI.history().then(setData).catch(() => setData({ items: [], months: [], accountant_email: '' }));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const sendNow = async () => {
    setBusy(true);
    try { const r = await exportAPI.sendNow(month || data.months[0]?.month); toast.success(r.status === 'sent' ? `Export ${r.label} envoyé à ${r.sent_to}` : `Export ${r.label} généré (${STATUS[r.status]?.[1] || r.status})`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-lg shadow-sm p-5 mb-6 border-l-4 border-[#1e3a5f]" data-testid="export-auto-block">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <CalendarClock size={26} className="text-[#1e3a5f] mt-0.5" />
          <div>
            <p className="font-semibold text-gray-900">Envoi automatique au comptable</p>
            <p className="text-sm text-gray-500">Le 1er de chaque mois à 7h, l'export Excel du mois écoulé est généré et un lien de téléchargement est envoyé par e-mail{data.accountant_email ? <> à <strong data-testid="export-auto-recipient">{data.accountant_email}</strong></> : ''}.</p>
            {!data.accountant_email && <p className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1" data-testid="export-auto-no-recipient"><AlertCircle size={12} /> Aucun e-mail de comptable : renseignez-le dans Réglages → Entreprise pour activer l'envoi.</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={month || data.months[0]?.month || ''} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded border border-gray-300 bg-white px-2 text-sm capitalize" data-testid="export-auto-month">{data.months.map((m) => <option key={m.month} value={m.month}>{m.label}</option>)}</select>
          <Button onClick={sendNow} disabled={busy || !data.accountant_email} className="bg-[#1e3a5f] hover:bg-[#16304f]" data-testid="export-auto-send-now">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />} Envoyer maintenant</Button>
        </div>
      </div>
      {data.items.length > 0 && (
        <div className="overflow-x-auto"><table className="mt-4 w-full text-sm" data-testid="export-auto-history">
          <thead className="text-xs text-gray-500 uppercase"><tr><th className="py-1 text-left">Période</th><th className="py-1 text-left">Généré le</th><th className="py-1 text-right">Factures</th><th className="py-1 text-right">Avoirs</th><th className="py-1 text-right">CA TTC</th><th className="py-1 text-left">Destinataire</th><th className="py-1 text-right">Fichier</th></tr></thead>
          <tbody className="divide-y">
            {data.items.map((it) => (
              <tr key={it.id} data-testid={`export-auto-row-${it.id}`}>
                <td className="py-1.5 capitalize font-medium text-gray-900">{it.label} <span className="text-[10px] font-normal text-gray-400">{it.trigger === 'cron' ? 'auto' : 'manuel'}</span></td>
                <td className="py-1.5 text-gray-600">{new Date(it.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                <td className="py-1.5 text-right">{it.summary?.invoices}</td>
                <td className="py-1.5 text-right">{it.summary?.credit_notes}</td>
                <td className="py-1.5 text-right">{eur(it.summary?.revenue_ttc)}</td>
                <td className={`py-1.5 text-xs ${STATUS[it.status]?.[0] || ''}`}><span className="inline-flex items-center gap-1" data-testid={`export-auto-status-${it.id}`}>{it.status === 'sent' ? <CheckCircle size={11} /> : <AlertCircle size={11} />} {STATUS[it.status]?.[1] || it.status}{it.sent_to ? ` · ${it.sent_to}` : ''}</span></td>
                <td className="py-1.5 text-right"><a href={`${window.location.origin}${it.url}`} className="inline-flex items-center gap-1 text-[#1e3a5f] underline text-xs" download data-testid={`export-auto-download-${it.id}`}><Download size={12} /> .xlsx</a></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
};

export default AdminExportAuto;
